import { Box, useTheme } from "@mui/material";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { ListKit } from "@tiptap/extension-list";
import Mention from "@tiptap/extension-mention";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import axios from "axios";
import type { ChipOption } from "components/editor/email-editor";
import { uploadChatAttachments, useGetTicket } from "controller";
import { useGetCustomerMembers } from "controller/hooks/customer/useCustomerMembers";
import { useMainStore } from "controller/store/mainStore";
import { useSetAtom } from "jotai";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AccessSpecifier, AttachmentResultType } from "shared/types";
import type { ChatMember } from "shared/types/chatMembers";
import type { GetTicketByIdResponse } from "shared/types/serviceDesk";
import { getFileExtensionFromMimeType, isUrl } from "utilz/helpers";
import { v4 } from "uuid";
import {
  chatVariableChipOptions,
  resolveChatVariable,
} from "./chatVariableOptions";
import { ChatVariable } from "./chatVariableExtension";
import { createChatVariableSuggestion } from "./chatVariableSuggestion";
import {
  createMentionSuggestion,
  type MentionItem,
} from "./ChatMentionSuggestion";
import { setChatImageUploadError } from "./store";

// ─── Types ───────────────────────────────────────────────────────────

export interface ChatMessageInputRef {
  clear: () => void;
  handleExternalSend: () => void;
  getEditor: () => Editor | null;
  focus: () => void;
}

interface TipTapChatInputProps {
  serviceId: string;
  serviceType: string;
  isDisabled: boolean;
  sendMessageAccessSpecifier: AccessSpecifier;
  isSendingMessage: boolean;
  isFileUploading?: boolean;
  hasExternalAttachments?: boolean;
  members: ChatMember[];
  isPinnedChat?: boolean;
  /** Initial editor HTML (e.g. a restored draft). Applied only at creation. */
  initialContent?: string;
  onSend: (params: {
    messageText: string;
    attachmentArray: Array<AttachmentResultType>;
    mentionedUsers?: string[];
    mentionedEmails?: string[];
  }) => void;
  toggleUploadingStatus: (value: boolean) => void;
  onContentChange?: (isEmpty: boolean) => void;
  /** Fired on every content change with the current editor HTML (draft save). */
  onDraftChange?: (html: string) => void;
  onMentionPresenceChange?: (presence: MentionPresence) => void;
  onFocusChange?: (focused: boolean) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────

export const getPlaceholderText = (
  mode: AccessSpecifier,
  isPinnedChat = false,
): string => {
  if (isPinnedChat) {
    return "Type a message...";
  }
  switch (mode) {
    case "public":
      return "Messaging in public mode (Visible to customer)";
    case "private":
      return "Messaging in private mode (only your team will see it)";
    case "self":
      return "Messaging in self mode (only you will see it)";
    default:
      return "Type a message...";
  }
};

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

const dataURLtoBlob = (dataurl: string): Blob => {
  const arr = dataurl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) {
    throw new Error("Invalid data URL");
  }
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/** Check whether the resolved position is inside a list/task item */
const isInsideListItem = (
  $from: { depth: number; node: (depth: number) => { type: { name: string } } },
): boolean => {
  for (let d = $from.depth; d >= 0; d--) {
    const name = $from.node(d).type.name;
    if (name === "listItem" || name === "taskItem") return true;
  }
  return false;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const extractMentions = (
  editor: Editor,
): { mentionedUsers: string[]; mentionedEmails: string[] } => {
  const mentionedUsers: string[] = [];
  const mentionedEmails: string[] = [];
  const json = editor.getJSON();

  const walk = (node: {
    type?: string;
    attrs?: Record<string, unknown>;
    content?: unknown[];
  }) => {
    if (node.type === "mention") {
      const id = node.attrs?.id as string | undefined;
      if (id) {
        if (EMAIL_REGEX.test(id)) {
          mentionedEmails.push(id);
        } else {
          mentionedUsers.push(id);
        }
      }
    }
    if (Array.isArray(node.content)) {
      (node.content as (typeof node)[]).forEach(walk);
    }
  };

  walk(json as Parameters<typeof walk>[0]);
  return {
    mentionedUsers: [...new Set(mentionedUsers)],
    mentionedEmails: [...new Set(mentionedEmails)],
  };
};

export interface MentionPresence {
  hasMention: boolean;
  hasNonResourceMention: boolean;
}

const getMentionPresence = (
  editor: Editor,
  members: ChatMember[],
): MentionPresence => {
  const { mentionedUsers, mentionedEmails } = extractMentions(editor);

  const hasMention =
    mentionedUsers.length > 0 || mentionedEmails.length > 0;

  // Email mentions are always non-resource
  if (mentionedEmails.length > 0) {
    return { hasMention: true, hasNonResourceMention: true };
  }

  // Check if any mentioned user is external or customer (non-resource)
  const memberMap = new Map(members.map((m) => [m.user_id, m]));
  const hasNonResourceMention = mentionedUsers.some((userId) => {
    const type = memberMap.get(userId)?.member_type;
    return type === "external" || type === "customer";
  });

  return { hasMention, hasNonResourceMention };
};

// ─── Component ───────────────────────────────────────────────────────

export const TipTapChatInput = forwardRef<
  ChatMessageInputRef,
  TipTapChatInputProps
>(
  (
    {
      serviceId,
      serviceType,
      isDisabled,
      sendMessageAccessSpecifier,
      isSendingMessage,
      isFileUploading,
      hasExternalAttachments = false,
      members,
      isPinnedChat = false,
      initialContent,
      onSend,
      toggleUploadingStatus,
      onContentChange,
      onDraftChange,
      onMentionPresenceChange,
      onFocusChange,
    },
    ref,
  ) => {
    const theme = useTheme();
    const setImageUploadError = useSetAtom(setChatImageUploadError);
    const [attachmentArray, setAttachmentArray] = useState<
      Array<AttachmentResultType>
    >([]);
    const totalImgsRef = useRef(0);
    const accessSpecifierRef = useRef(sendMessageAccessSpecifier);
    const membersRef = useRef(members);

    // Keep refs in sync
    useEffect(() => {
      accessSpecifierRef.current = sendMessageAccessSpecifier;
    }, [sendMessageAccessSpecifier]);

    useEffect(() => {
      membersRef.current = members;
    }, [members]);

    // ─── Customer members for mention suggestions ───────────────
    const customerId = useMainStore(
      (state) => state.ticketDetails?.customerId,
    ) as string | undefined;

    const [mentionQuery, setMentionQuery] = useState("");
    const [debouncedMentionQuery, setDebouncedMentionQuery] = useState("");

    useEffect(() => {
      const timer = setTimeout(
        () => setDebouncedMentionQuery(mentionQuery),
        300,
      );
      return () => clearTimeout(timer);
    }, [mentionQuery]);

    const { data: customerMembersData } = useGetCustomerMembers(
      customerId ?? "",
      debouncedMentionQuery || undefined,
      Boolean(customerId) && debouncedMentionQuery.length > 0,
    );

    const customerMentionItems = useMemo((): MentionItem[] => {
      if (!customerMembersData) return [];

      const resources = customerMembersData.resources.result.map((r) => ({
        id: r.mapping_id,
        label: r.display_name || `${r.first_name} ${r.last_name}`.trim(),
        email: r.email,
        profileImage: null,
        type: "member" as const,
      }));

      const users = customerMembersData.users.result.map((u) => ({
        id: u.mapping_id,
        label: u.display_name || `${u.first_name} ${u.last_name}`.trim(),
        email: u.email,
        profileImage: null,
        type: "member" as const,
      }));

      return [...resources, ...users];
    }, [customerMembersData]);

    const customerMembersRef = useRef<MentionItem[]>([]);
    useEffect(() => {
      customerMembersRef.current = customerMentionItems;
    }, [customerMentionItems]);

    const onQueryChangeRef = useRef((query: string) => {
      setMentionQuery(query);
    });

    // ─── Variable chip resolution (ticket-only) ─────────────────
    // Fetch the current ticket data so that typing `{{` can insert
    // resolved literal values (customer name, priority, etc.).
    // The query is already cached by the parent ticket detail screen,
    // so this does NOT trigger an extra network request.
    const isTicketService = serviceType === "ticket";
    const { data: ticketData } = useGetTicket(
      isTicketService ? serviceId : undefined,
    );

    const ticketDataRef = useRef<GetTicketByIdResponse | null | undefined>(
      ticketData as GetTicketByIdResponse | undefined,
    );
    useEffect(() => {
      ticketDataRef.current = ticketData as GetTicketByIdResponse | undefined;
    }, [ticketData]);

    // Disable suggestion entirely for non-ticket service types by
    // providing an empty options array.
    const chipOptionsRef = useRef<ChipOption[]>(
      isTicketService ? chatVariableChipOptions : [],
    );
    useEffect(() => {
      chipOptionsRef.current = isTicketService ? chatVariableChipOptions : [];
    }, [isTicketService]);

    // Stable resolver that always reads the latest ticket via the ref.
    const resolveRef = useRef<(id: string) => string>((id: string) =>
      resolveChatVariable(id, ticketDataRef.current),
    );

    // ─── Upload image ───────────────────────────────────────────

    const uploadImage = useCallback(
      async (src: string, editor: Editor) => {
        try {
          totalImgsRef.current += 1;
          toggleUploadingStatus(true);

          let blob: Blob;
          if (isUrl(src)) {
            const externalData = await axios.get(src, {
              responseType: "blob",
            });
            if (externalData.status === 200) {
              blob = externalData.data;
            } else {
              return;
            }
          } else {
            blob = dataURLtoBlob(src);
          }

          const id = v4();
          const formData = new FormData();
          formData.append(
            "files",
            blob,
            `${serviceId}_${serviceType}_${id}.${getFileExtensionFromMimeType(
              blob.type,
            )}`,
          );

          uploadChatAttachments({
            formData,
            serviceId,
            serviceType,
          }).subscribe({
            next: ({ response, status }) => {
              if (status === "COMPLETE") {
                totalImgsRef.current -= 1;
                if (totalImgsRef.current === 0) {
                  toggleUploadingStatus(false);
                }
                if (response?.success && response?.statusCode === 200) {
                  const imgData = response.data?.[0];
                  if (imgData) {
                    // Insert image at current cursor position (inline only, not as attachment)
                    editor.chain().focus().setImage({ src: imgData.url }).run();
                  }
                }
              }
            },
          });
        } catch (error) {
          totalImgsRef.current -= 1;
          if (totalImgsRef.current === 0) {
            toggleUploadingStatus(false);
          }
          console.error("Chat image upload failed", error);
        }
      },
      [serviceId, serviceType, toggleUploadingStatus],
    );

    // ─── TipTap Editor ─────────────────────────────────────────

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          hardBreak: false,
        }),
        Strike,
        ListKit.configure({
          taskList: false,
          taskItem: false,
        }),
        TaskList,
        TaskItem.configure({
          nested: false,
        }),
        Placeholder.configure({
          placeholder: () =>
            getPlaceholderText(accessSpecifierRef.current, isPinnedChat),
        }),
        Mention.configure({
          HTMLAttributes: { class: "chat-mention" },
          suggestion: createMentionSuggestion(
            membersRef,
            customerMembersRef,
            onQueryChangeRef,
            accessSpecifierRef,
            serviceType === "task",
          ),
          renderLabel: ({ node }) => `@${node.attrs.label}`,
        }),
        ChatVariable.configure({
          suggestion: createChatVariableSuggestion(chipOptionsRef, resolveRef),
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        Image.configure({
          inline: true,
          HTMLAttributes: {
            style:
              "max-width: 180px; max-height: 180px; object-fit: contain; border-radius: 4px;",
          },
        }),
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
      ],
      content: initialContent ?? "",
      editable: !isDisabled && !isSendingMessage,
      editorProps: {
        handleKeyDown: (view, event) => {
          // Cmd+Enter (Mac) / Ctrl+Enter (Win) to send
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            return true;
          }
          // Shift+Enter → new paragraph (block) instead of <br>
          // Skip when inside a list so TipTap's native list-exit still works
          if (event.key === "Enter" && event.shiftKey) {
            const { state } = view;
            const { $from } = state.selection;
            if (!isInsideListItem($from)) {
              event.preventDefault();
              const tr = state.tr.split($from.pos);
              view.dispatch(tr);
              return true;
            }
          }
          return false;
        },
        handlePaste: (view, event) => {
          const items = event.clipboardData?.items;
          if (!items) return false;

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf("image") !== -1) {
              event.preventDefault();
              const file = item.getAsFile();
              if (!file) continue;

              if (file.size > MAX_IMAGE_SIZE) {
                const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
                setImageUploadError(
                  `File exceeds 10MB limit (${sizeMB}MB)`,
                );
                return true;
              }

              const reader = new FileReader();
              reader.onloadend = () => {
                const base64Image = reader.result as string;
                if (editor) {
                  uploadImage(base64Image, editor);
                }
              };
              reader.readAsDataURL(file);
              return true;
            }
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        onContentChange?.(ed.isEmpty);
        onDraftChange?.(ed.getHTML());
        onMentionPresenceChange?.(
          getMentionPresence(ed, membersRef.current ?? []),
        );
      },
      onFocus: () => {
        onFocusChange?.(true);
      },
      onBlur: () => {
        onFocusChange?.(false);
      },
    });

    // ─── Notify parent of initial empty state ─────────────────────
    useEffect(() => {
      if (editor) {
        onContentChange?.(editor.isEmpty);
        onMentionPresenceChange?.(
          getMentionPresence(editor, membersRef.current ?? []),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    // ─── Handle send (from Cmd+Enter / Ctrl+Enter) ──────────────

    const handleSend = useCallback(() => {
      if (!editor || isDisabled || isSendingMessage || isFileUploading) return;
      if (
        editor.isEmpty &&
        attachmentArray.length === 0 &&
        !hasExternalAttachments
      )
        return;
      const html = editor.getHTML();
      const { mentionedUsers, mentionedEmails } = extractMentions(editor);
      onSend({
        messageText: html,
        attachmentArray,
        mentionedUsers,
        mentionedEmails,
      });
    }, [
      editor,
      isDisabled,
      isSendingMessage,
      isFileUploading,
      hasExternalAttachments,
      onSend,
      attachmentArray,
    ]);

    // Wire Cmd+Enter / Ctrl+Enter to send — we use a capture-phase
    // listener on the wrapper so the send fires before TipTap
    // processes the keydown.
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          e.stopPropagation();
          handleSend();
        }
      },
      [handleSend],
    );

    // ─── Update editable state when props change ────────────────

    useEffect(() => {
      if (editor) {
        const editable = !isDisabled && !isSendingMessage;
        editor.setEditable(editable);
        if (editable) {
          editor.commands.focus();
        }
      }
    }, [editor, isDisabled, isSendingMessage]);

    // Force placeholder re-render when access specifier changes
    useEffect(() => {
      if (editor) {
        editor.view.dispatch(editor.state.tr);
      }
    }, [editor, sendMessageAccessSpecifier]);

    // ─── Imperative Handle ──────────────────────────────────────

    useImperativeHandle(ref, () => ({
      clear: () => {
        editor?.commands.clearContent();
        setAttachmentArray([]);
      },
      handleExternalSend: () => {
        if (!editor || isDisabled || isSendingMessage || isFileUploading)
          return;
        if (
          editor.isEmpty &&
          attachmentArray.length === 0 &&
          !hasExternalAttachments
        )
          return;
        const html = editor.getHTML();
        const { mentionedUsers, mentionedEmails } = extractMentions(editor);
        onSend({
          messageText: html,
          attachmentArray,
          mentionedUsers,
          mentionedEmails,
        });
      },
      getEditor: () => editor,
      focus: () => {
        editor?.commands.focus("end");
      },
    }));

    // ─── Render ─────────────────────────────────────────────────

    return (
      <Box
        onKeyDownCapture={handleKeyDown}
        sx={{
          width: "100%",
          minHeight: "60px",
          maxHeight: "200px",
          overflow: "auto",
          "& .tiptap": {
            outline: "none",
            minHeight: "60px",
            ...theme.typography["body-medium"],
            padding: "8px",
            wordBreak: "break-word",
          },
          "& .tiptap p.is-editor-empty:first-child::before": {
            content: "attr(data-placeholder)",
            color: theme.palette.lightBg.low,
            float: "left",
            height: 0,
            pointerEvents: "none",
          },
          // Mention chip styling
          "& .chat-mention": {
            ...theme.typography["tag-label-xs"],
            backgroundColor: "#D2DDFF",
            color: "#5563F5",
            borderRadius: "4px",
            padding: "1px 6px",
          },
          // Pasted image styling
          "& .tiptap img": {
            maxWidth: "180px",
            maxHeight: "180px",
            objectFit: "contain",
            borderRadius: "4px",
          },
          // List styling
          "& .tiptap ul, & .tiptap ol": {
            paddingLeft: "20px",
          },
          "& .tiptap ul[data-type='taskList']": {
            listStyle: "none",
            paddingLeft: "4px",
          },
          "& .tiptap blockquote": {
            borderLeft: `3px solid ${theme.palette.neutral["015"]}`,
            paddingLeft: "12px",
            margin: "4px 0",
            color: theme.palette.lightBg.medium,
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    );
  },
);
